import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { Tab } from 'src/thunks/classes';
import { RootState } from 'src/store/index';

import { selector as classSelector } from 'src/store/model/classes';

export type State = {
  loading: boolean;
};

export const adapter = createEntityAdapter<Model.ClassUser>({
  selectId: (model) => model.id,
});

const { reducer, actions } = createSlice({
  name: 'class_user',
  initialState: adapter.getInitialState<State>({ loading: false }),
  reducers: {
    updateEntities(state, action: PayloadAction<Model.ClassUser[]>) {
      adapter.upsertMany(state, action);
    },
    updateEntity(state, action: PayloadAction<Model.ClassUser>) {
      adapter.upsertOne(state, action);
    },
    updateLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
  },
});

export const selector = adapter.getSelectors(
  (state: RootState) => state.model.class_user,
);

export const isStudent = createSelector(
  (state: RootState) => state.ui.app.me,
  (user) => {
    return !!user?.roles?.every((user) => user.name === 'Student');
  },
);

export const userCoursesSelector = createSelector(
  (state: RootState) => {
    const classUsers = selector.selectAll(state);
    const loading = state.model.class_user.loading;

    return { classUsers, loading };
  },
  (state: RootState, tab: Tab | undefined) => ({
    tab,
    user: state.ui.app.me,
  }),
  classSelector.selectAll,
  ({ classUsers = [], loading }, { tab, user }, classes) => {
    if (tab === 'my_courses') {
      const isStudent = user?.roles?.every((role) => role.name === 'Student');

      const userCourses = classUsers
        .filter((class_user) => {
          return (
            class_user?.user_id === user?.id &&
            (isStudent ? class_user.is_paid : true)
          );
        })
        .map((class_user) => {
          return {
            ...class_user,
            class: {
              ...class_user.class,
              ...classes.find(({ id }) => class_user?.class_id === id),
            },
          };
        });

      const userCoursesWithPublic = userCourses.filter(
        (course) => !!course.class.is_published,
      );

      return {
        loading,
        data: userCoursesWithPublic,
      };
    }

    return { loading, data: classUsers };
  },
);

export const selectClassUserByClassId = createSelector(
  selector.selectAll,
  (state: RootState) => state.ui.app.me,
  (state: RootState, class_id: string) =>
    classSelector.selectById(state, class_id),
  (classUsers, user, course) => {
    const matches = classUsers.filter(
      (class_user) =>
        class_user?.class_id === course?.id && class_user?.user_id === user?.id,
    );
    const class_user = matches.find((c) => c?.is_paid) || matches[0];

    return { ...class_user, class: course };
  },
);

export const selectUserMentionByClassId = createSelector(
  selector.selectAll,
  (state: RootState, class_id?: string | null) => class_id,
  (class_users, class_id) => {
    return class_users
      .filter((class_user) => class_user?.class_id === class_id)
      .map((class_user) => {
        return {
          label: class_user.user?.name,
          value: class_user.user_id,
          profile_picture: class_user.user?.profile_picture,
        };
      });
  },
);

export { reducer };
export default actions;
